import React from 'react';
import { motion } from 'framer-motion';

export default function EmptyState({ text, hideOrb }: { text: string; hideOrb?: boolean }): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.48, ease: [0.22, 0.61, 0.36, 1] }}
      className="relative w-full flex flex-col items-center justify-center py-24 px-8 text-center overflow-hidden"
      style={{ borderRadius: 'var(--r-xl)' }}
    >
      {/* Scan-wave texture */}
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='32' viewBox='0 0 240 32'><path d='M0 16 C 40 8, 80 8, 120 16 S 200 24, 240 16' fill='none' stroke='%232AA4A6' stroke-opacity='0.18' stroke-width='1.1' stroke-linecap='round'/></svg>")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '240px 32px',
        }}
      />

      {/* Orb glow */}
      {!hideOrb && (
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.18, 0.28, 0.18] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute w-64 h-64 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 40% 40%, var(--c-kivo-teal) 0%, var(--c-coral) 55%, transparent 70%)',
            filter: 'blur(64px)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}

      {/* Icon ring */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.12, duration: 0.44, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative mb-8 w-[72px] h-[72px] rounded-full flex items-center justify-center"
        style={{
          background: 'var(--c-cream)',
          border: '1px solid rgba(42,164,166,0.18)',
          boxShadow: 'var(--sh-lifted)',
        }}
      >
        <span className="material-icons-round text-3xl" style={{ color: 'var(--c-kivo-teal)' }}>spa</span>
      </motion.div>

      {/* Heading — Fraunces display */}
      <motion.h3
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="font-display font-medium text-[22px] leading-[1.2] text-[var(--ink)] mb-3"
        style={{ letterSpacing: '-0.005em' }}
      >
        Nothing to show{' '}
        <em className="not-italic" style={{ color: 'var(--c-kivo-teal)' }}>yet</em>
      </motion.h3>

      {/* Body — Inter */}
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.28, duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="font-text text-[14px] leading-[1.55] max-w-[34ch]"
        style={{ color: 'rgba(31,36,33,0.55)' }}
      >
        {text}
      </motion.p>

      {/* Decorative rule */}
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.56, ease: [0.22, 0.61, 0.36, 1] }}
        className="mt-10 w-28 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(42,164,166,0.5), transparent)' }}
      />

      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="mt-4 font-text text-[11px] tracking-[0.18em] uppercase"
        style={{ color: 'rgba(31,36,33,0.28)' }}
      >
        kivo.skin
      </motion.span>
    </motion.div>
  );
}
